import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { protocolosAPI, animalesAPI } from '../services/api';
import { toast } from 'sonner';

export default function ProtocolosPage() {
  const { user } = useAuth();
  const [protocolos, setProtocolos] = useState([]);
  const [tareas, setTareas] = useState([]);
  const [plantillas, setPlantillas] = useState([]);
  const [hembras, setHembras] = useState([]);
  const [toros, setToros] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ plantilla: 'iatf_convencional', fecha_inicio: new Date().toISOString().slice(0, 10), animales_ids: [], toro_id: '', semen: '', nombre: '' });

  const loadData = useCallback(async () => {
    if (!user?.establecimiento_id) return;
    setLoading(true);
    try {
      const [p, r, pl, a] = await Promise.all([
        protocolosAPI.listar({ establecimiento_id: user.establecimiento_id }),
        protocolosAPI.recordatorios(user.establecimiento_id, 7),
        protocolosAPI.plantillas(),
        animalesAPI.getAll({ establecimiento_id: user.establecimiento_id, page_size: 500, estado: 'activo' }),
      ]);
      setProtocolos(p.data.protocolos);
      setTareas(r.data.tareas);
      setPlantillas(pl.data.plantillas);
      const animales = a.data.data || [];
      setHembras(animales.filter(x => x.sexo === 'hembra' && ['vaca', 'vaquilla'].includes(x.categoria)));
      setToros(animales.filter(x => x.categoria === 'toro'));
    } catch { toast.error('Error cargando protocolos'); }
    setLoading(false);
  }, [user]);

  useEffect(() => { loadData(); }, [loadData]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.animales_ids.length) { toast.error('Seleccioná al menos una hembra'); return; }
    try {
      await protocolosAPI.crear({ ...form, toro_id: form.toro_id || null, establecimiento_id: user.establecimiento_id });
      toast.success('Protocolo creado con calendario de fechas');
      setShowForm(false);
      setForm({ ...form, animales_ids: [], nombre: '' });
      loadData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Error al crear protocolo'); }
  };

  const completar = async (protId, pasoId) => {
    try {
      const res = await protocolosAPI.completarPaso(protId, pasoId);
      toast.success(res.data.message);
      loadData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Error'); }
  };

  const eliminar = async (id) => {
    try { await protocolosAPI.eliminar(id); toast.success('Protocolo eliminado'); loadData(); } catch { toast.error('Error'); }
  };

  if (loading) return (
    <div className="p-6 flex items-center justify-center min-h-[60vh]">
      <div className="inline-block w-8 h-8 border-4 border-pink-600 border-t-transparent rounded-full animate-spin" />
    </div>
  );

  const plantillaSel = plantillas.find(p => p.id === form.plantilla);

  return (
    <div className="p-4 sm:p-6 space-y-6" data-testid="protocolos-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-xl sm:text-2xl font-bold text-gray-900">Protocolos IATF</h2>
          <p className="text-sm text-gray-500 mt-1">Calendario reproductivo guiado con fechas automáticas</p>
        </div>
        <button onClick={() => setShowForm(!showForm)} className="px-4 py-2 bg-pink-600 text-white text-sm font-semibold rounded-lg hover:bg-pink-700 transition" data-testid="btn-nuevo-protocolo">+ Nuevo Protocolo</button>
      </div>

      {tareas.length > 0 && (
        <div className="bg-amber-50 border-l-4 border-amber-500 rounded-r-xl p-4" data-testid="recordatorios-card">
          <h3 className="font-bold text-amber-900 mb-2">📅 Próximas tareas ({tareas.length})</h3>
          {tareas.slice(0, 5).map(t => (
            <div key={t.paso_id} className="flex flex-wrap items-center justify-between gap-2 text-sm py-1">
              <span className={t.vencido ? 'text-red-700 font-semibold' : 'text-amber-900'}>
                {t.vencido ? '⚠ VENCIDO · ' : t.hoy ? '🔔 HOY · ' : ''}{t.fecha} — {t.titulo} <span className="text-amber-700">({t.protocolo_nombre}, {t.cantidad_animales} animales)</span>
              </span>
              <button onClick={() => completar(t.protocolo_id, t.paso_id)} className="text-xs px-3 py-1 bg-amber-600 text-white rounded-lg hover:bg-amber-700" data-testid={`completar-tarea-${t.paso_id}`}>Marcar hecho</button>
            </div>
          ))}
        </div>
      )}

      {showForm && (
        <form onSubmit={submit} className="bg-white rounded-xl shadow-sm p-5 space-y-4" data-testid="form-protocolo">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            <select value={form.plantilla} onChange={e => setForm({ ...form, plantilla: e.target.value })} className="border rounded-lg px-3 py-2 text-sm" data-testid="protocolo-plantilla">
              {plantillas.map(p => <option key={p.id} value={p.id}>{p.nombre}</option>)}
            </select>
            <input required type="date" value={form.fecha_inicio} onChange={e => setForm({ ...form, fecha_inicio: e.target.value })} className="border rounded-lg px-3 py-2 text-sm" data-testid="protocolo-fecha" />
            <select value={form.toro_id} onChange={e => setForm({ ...form, toro_id: e.target.value })} className="border rounded-lg px-3 py-2 text-sm" data-testid="protocolo-toro">
              <option value="">Toro / semen sin asignar</option>
              {toros.map(t => <option key={t.id} value={t.id}>{t.VID}</option>)}
            </select>
            <input placeholder="Semen / pajuela (opcional)" value={form.semen} onChange={e => setForm({ ...form, semen: e.target.value })} className="border rounded-lg px-3 py-2 text-sm" data-testid="protocolo-semen" />
          </div>

          {plantillaSel && (
            <div className="bg-pink-50 rounded-lg p-3 text-xs text-pink-900">
              <strong>Calendario que se generará:</strong> {plantillaSel.pasos.map(p => `D${p.dia} ${p.titulo.split(' - ')[1] || ''}`).join(' → ')}
            </div>
          )}

          <p className="text-sm font-semibold text-gray-700">Hembras aptas — vacas y vaquillas activas ({form.animales_ids.length} seleccionadas):</p>
          {hembras.length === 0 ? (
            <p className="text-sm text-gray-400">No hay vacas ni vaquillas activas. Cargá tus hembras en la sección Animales primero.</p>
          ) : (
            <div className="max-h-40 overflow-y-auto border rounded-lg p-2 grid grid-cols-2 sm:grid-cols-4 gap-1">
              <label className="flex items-center gap-2 text-sm px-2 py-1 font-semibold text-pink-700 cursor-pointer">
                <input type="checkbox" checked={form.animales_ids.length === hembras.length}
                  onChange={e => setForm({ ...form, animales_ids: e.target.checked ? hembras.map(h => h.id) : [] })} data-testid="protocolo-select-all" />
                Todas ({hembras.length})
              </label>
              {hembras.map(h => (
                <label key={h.id} className="flex items-center gap-2 text-sm px-2 py-1 hover:bg-gray-50 rounded cursor-pointer">
                  <input type="checkbox" checked={form.animales_ids.includes(h.id)}
                    onChange={e => setForm({ ...form, animales_ids: e.target.checked ? [...form.animales_ids, h.id] : form.animales_ids.filter(x => x !== h.id) })} />
                  <span>{h.VID} <span className="text-gray-400">({h.categoria})</span></span>
                </label>
              ))}
            </div>
          )}
          <button type="submit" className="px-5 py-2 bg-pink-700 text-white text-sm font-semibold rounded-lg hover:bg-pink-800" data-testid="protocolo-guardar">Crear protocolo y calendario</button>
        </form>
      )}

      {protocolos.length === 0 && !showForm ? (
        <div className="bg-white rounded-xl shadow-sm p-8 text-center" data-testid="protocolos-empty">
          <p className="text-gray-500 mb-3">Todavía no tenés protocolos. Creá tu primer <strong>protocolo IATF</strong> y el sistema genera automáticamente las fechas de dispositivo, retiro, inseminación y diagnóstico.</p>
          <button onClick={() => setShowForm(true)} className="px-4 py-2 bg-pink-600 text-white text-sm font-semibold rounded-lg hover:bg-pink-700">Crear mi primer protocolo</button>
        </div>
      ) : (
        <div className="space-y-4">
          {protocolos.map(prot => (
            <div key={prot.id} className="bg-white rounded-xl shadow-sm p-5" data-testid={`protocolo-${prot.id}`}>
              <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
                <div>
                  <h3 className="font-bold text-gray-900">{prot.nombre}
                    <span className={`ml-2 text-xs px-2 py-0.5 rounded-full ${prot.estado === 'activo' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'}`}>{prot.estado}</span>
                  </h3>
                  <p className="text-xs text-gray-500">Inicio: {prot.fecha_inicio} · {(prot.animales_ids || []).length} hembras{prot.semen ? ` · Semen: ${prot.semen}` : ''}</p>
                </div>
                <button onClick={() => eliminar(prot.id)} className="text-xs text-gray-400 hover:text-red-600" data-testid={`del-protocolo-${prot.id}`}>Eliminar</button>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                {(prot.pasos || []).map(paso => (
                  <div key={paso.id} className={`rounded-lg border p-3 ${paso.estado === 'completado' ? 'bg-green-50 border-green-200' : 'bg-gray-50 border-gray-200'}`}>
                    <p className="text-xs font-bold text-gray-700">{paso.titulo}</p>
                    <p className="text-xs text-gray-500 mt-0.5">{paso.fecha}</p>
                    <p className="text-[11px] text-gray-400 mt-1 line-clamp-2">{paso.descripcion}</p>
                    {paso.estado === 'completado' ? (
                      <p className="text-xs text-green-700 font-semibold mt-2">✓ Completado</p>
                    ) : (
                      <button onClick={() => completar(prot.id, paso.id)} className="mt-2 text-xs px-3 py-1 bg-gray-900 text-white rounded-lg hover:bg-gray-700" data-testid={`completar-${paso.id}`}>
                        {paso.es_inseminacion ? 'Completar + crear servicios' : 'Completar'}
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
